import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit, Trash2, Users } from 'lucide-react';
import toast from 'react-hot-toast';
import { api } from '../services/api';
import TeamForm from '../components/TeamForm';

const Teams = () => {
  const [showForm, setShowForm] = useState(false);
  const [editingTeam, setEditingTeam] = useState(null);
  const [filter, setFilter] = useState({ search: '' });
  const [sorting, setSorting] = useState({ sort_by: 'name', sort_order: 'asc' });
  const queryClient = useQueryClient();

  const { data: teams = [], isLoading } = useQuery({
    queryKey: ['teams', filter, sorting],
    queryFn: () => {
      const params = {
        ...sorting,
        ...(filter.search ? { search: filter.search } : {})
      };
      return api.getTeams(params);
    }
  });

  const createMutation = useMutation({
    mutationFn: api.createTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setShowForm(false);
      toast.success('Team created successfully!');
    },
    onError: () => toast.error('Failed to create team'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => api.updateTeam(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      setShowForm(false);
      setEditingTeam(null);
      toast.success('Team updated successfully!');
    },
    onError: () => toast.error('Failed to update team'),
  });

  const deleteMutation = useMutation({
    mutationFn: api.deleteTeam,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['teams'] });
      toast.success('Team deleted successfully!');
    },
    onError: () => toast.error('Failed to delete team'),
  });

  const handleSubmit = (data) => {
    // Clean the data - convert empty strings to null and remove empty fields
    const cleanData = Object.fromEntries(
      Object.entries(data).map(([key, value]) => [
        key, 
        value === '' || value === null ? null : value
      ]).filter(([_, value]) => value !== null)
    );
    
    if (editingTeam) {
      updateMutation.mutate({ id: editingTeam.id, data: cleanData });
    } else {
      createMutation.mutate(cleanData);
    }
  };

  const handleEdit = (team) => {
    setEditingTeam(team);
    setShowForm(true);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this team?')) {
      deleteMutation.mutate(id);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cricket-green"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Teams</h1>
        <button
          onClick={() => setShowForm(true)}
          className="bg-cricket-green text-white px-4 py-2 rounded-lg flex items-center space-x-2 hover:bg-green-600 transition-colors w-full sm:w-auto justify-center"
        >
          <Plus className="h-4 w-4" />
          <span>Add Team</span>
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4 border border-gray-200">
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <input
              type="text"
              value={filter.search}
              onChange={(e) => setFilter({ ...filter, search: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
              placeholder="Search teams..."
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
            <select
              value={sorting.sort_by}
              onChange={(e) => setSorting({ ...sorting, sort_by: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="name">Name</option>
              <option value="founded_year">Founded Year</option>
              <option value="created_at">Date Added</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
            <select
              value={sorting.sort_order}
              onChange={(e) => setSorting({ ...sorting, sort_order: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cricket-green focus:border-transparent"
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>

      {showForm ? (
        <TeamForm
          team={editingTeam}
          onSubmit={handleSubmit}
          onCancel={() => {
            setShowForm(false);
            setEditingTeam(null);
          }}
          isLoading={createMutation.isLoading || updateMutation.isLoading}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {teams.map((team) => (
          <div key={team.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-800">{team.name}</h3>
              <div className="flex space-x-2">
                <button
                  onClick={() => handleEdit(team)}
                  className="text-blue-500 hover:text-blue-700 p-1"
                >
                  <Edit className="h-4 w-4" />
                </button>
                <button
                  onClick={() => handleDelete(team.id)}
                  className="text-red-500 hover:text-red-700 p-1"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
            
            {team.description && (
              <p className="text-gray-600 mb-4">{team.description}</p>
            )}
            
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <div className="flex items-center space-x-1">
                <Users className="h-4 w-4" />
                <span>{team.player_count ?? team.players?.length ?? 0} players</span>
              </div>
              {team.founded_year && (
                <span>Founded {team.founded_year}</span>
              )}
            </div>
          </div>
        ))}
        </div>
      )}

      {!showForm && teams.length === 0 && (
        <div className="text-center py-12">
          <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-gray-600 mb-2">No teams yet</h3>
          <p className="text-gray-500 mb-4">Create your first team to get started</p>
          <button
            onClick={() => setShowForm(true)}
            className="bg-cricket-green text-white px-6 py-2 rounded-lg hover:bg-green-600 transition-colors"
          >
            Create Team
          </button>
        </div>
      )}
    </div>
  );
};

export default Teams;
